/**
 * Phase 8 · Cluster C-42 — Agent tool registry.
 *
 * Spec:  docs/ux/PHASE8_WRANGLE_TRANSFORMATION.md Addendum F §33.6
 * Smoke: docs/ux/PHASE8_VISUAL_TRACKER.md §8.6
 *
 * Narrow, typed, read-only tool registry the agent runner dispatches into.
 * Each tool declares its name, args/result zod schemas, required permissions
 * and an `invoke(ctx, args)` implementation; `toolRegistry.dispatch` parses
 * the args, runs the tool, validates the result, and logs one audit row to
 * `ggj_b2b_agent_tool_invocations` via `agentToolInvocationsService.log` —
 * success or failure.
 *
 * Only the runner (`agentRunnerService.streamReply`) may call `dispatch`.
 * The frontend never reaches the registry directly; tool-call intents come
 * either from the LLM's function-calling envelope or from a manual
 * injection the runner accepts on `streamReply`'s options. This keeps the
 * budget/permission surface auditable — every call is mediated.
 *
 * Phase-8 tool set (registered in ./internal.ts, C-42):
 *   - data.candidates.list
 *   - data.jobs.list
 *   - data.requisitions.list
 *
 * Connector tools (source.*) register in C-43 / C-44 behind the same API.
 */
import { z } from 'zod';
import { agentToolInvocationsService } from '@core/supabase/services/agentToolInvocationsService';

/**
 * Permission tags a tool declares. The runner (or an Edge Function in the
 * eventual server path) checks these against the caller's session before
 * dispatching. For Phase-8's three internal tools everything is
 * `internal:read`; the connector batches add `sourcing:read` later.
 */
export type AgentToolPermission = 'sourcing:read' | 'internal:read' | 'internal:write';

/**
 * Phase 8 · C-48 — read vs write capability classification.
 *
 * Every tool declares `capability`. Defaults to `'read'` so the C-42 tool
 * set (data.candidates.list, data.jobs.list, data.requisitions.list) and
 * every sourcing tool keep working without touching their definitions.
 *
 * The runner uses `capability` to decide plan/approve routing:
 *   - any `'write'` call → Plan card (unless single + reversible +
 *     autonomous mode is on and the tool is NOT on the safety list).
 *   - two or more `'read'` calls in one turn → still a Plan card,
 *     because parallel reads are usually a multi-step workflow too.
 */
export type AgentToolCapability = 'read' | 'write';

export interface AgentToolCtx {
  orgId: string;
  userId: string;
  threadId: string;
  turnId: string;
  /**
   * Phase 8 · C-48 — when this invocation is being dispatched as part of
   * an approved plan, the parent plan id + step index are threaded
   * through so the audit row (written by dispatch in its finally block)
   * can link back via `plan_id` / `step_index`.
   *
   * `approvalMode` is `'manual'` when the user clicked Approve on the
   * Plan card and `'auto'` when the runner executed a single reversible
   * write tool under autonomous mode (spec §34.2.C autonomous rules).
   * Undefined for legacy read-only dispatches so the C-42 audit schema
   * stays compatible.
   */
  planId?: string;
  stepIndex?: number;
  approvalMode?: 'manual' | 'auto';
}

/**
 * Typed result envelope returned by every C-48 write tool (and optionally
 * by read tools that want richer cards). The runner relies on these
 * fields to render step-result cards in the Plan UI and to persist a
 * compact summary in `ggj_b2b_agent_tool_invocations.result_summary`.
 */
export interface AgentWriteToolResult {
  ok: boolean;
  entityKind:
    | 'requisition'
    | 'candidate'
    | 'application'
    | 'interview'
    | 'email'
    | 'sequence'
    | 'shortlist'
    | 'outreach';
  entityId: string;
  entityLabel: string;
  /**
   * `true` when the action can be undone trivially (list, draft, stage
   * transition within the same owner's draft requisition). `false` for
   * anything that leaves the system of record (`write.emails.send`,
   * `write.outreach.sequence_start`, `write.applications.reject`).
   *
   * Drives the Reversible/Permanent badge on the Plan card and, with the
   * hardcoded safety list in `src/modules/agent/safety.ts`, whether the
   * runner is allowed to auto-execute a single call under autonomous
   * mode.
   */
  reversible: boolean;
  /**
   * Human-readable single-line summary the agent shows on the turn and
   * that the audit log persists as `result_summary` (≤ 500 chars). When
   * absent, the registry falls back to its generic `summarize()` helper.
   */
  message?: string;
  /** Optional provider-level detail rendered inside the step result card. */
  detail?: Record<string, unknown>;
}

export interface AgentTool<A = unknown, R = unknown> {
  name: string;
  description: string;
  argsSchema: z.ZodSchema<A>;
  resultSchema: z.ZodSchema<R>;
  permissions: AgentToolPermission[];
  /**
   * Phase 8 · C-48 — read vs write classification. Optional in the type
   * so pre-C-48 registrations don't need to change; the registry treats
   * an unset `capability` as `'read'` at dispatch time and when writing
   * the audit row.
   */
  capability?: AgentToolCapability;
  invoke(ctx: AgentToolCtx, args: A): Promise<R>;
}

/**
 * Human-readable single-line summary of a tool result — this is what the
 * LLM sees as "tool output" on the next turn and what the DB persists as
 * `result_summary`. We cap at 500 chars to match the column constraint in
 * migration 107; `agentToolInvocationsService.log` re-clamps defensively.
 */
function summarize(result: unknown): string {
  if (result == null) return '(no result)';
  if (typeof result === 'string') return result.slice(0, 500);
  if (typeof result === 'number' || typeof result === 'boolean') return String(result);

  // Prefer count-of-list framing for tools whose result is a wrapper around
  // an array (e.g. { candidates: [...] }) — that's what every Phase-8 data
  // tool returns and is the most useful signal to show beneath a turn.
  if (typeof result === 'object') {
    const rec = result as Record<string, unknown>;
    for (const key of Object.keys(rec)) {
      const v = rec[key];
      if (Array.isArray(v)) {
        const n = v.length;
        return `${n} ${key}${n === 1 ? '' : ''}`.slice(0, 500);
      }
    }
  }

  try {
    return JSON.stringify(result).slice(0, 500);
  } catch {
    return '[unserialisable result]';
  }
}

class ToolRegistry {
  private tools = new Map<string, AgentTool<unknown, unknown>>();

  register<A, R>(tool: AgentTool<A, R>): void {
    // Registering the same name twice is a developer error — the internal
    // file runs once at boot and connectors register at most once per kind.
    // We warn rather than throw so HMR (which re-executes module code) does
    // not break the whole app during dev.
    if (this.tools.has(tool.name)) {
      console.warn(`[toolRegistry] replacing existing tool "${tool.name}"`);
    }
    this.tools.set(tool.name, tool as AgentTool<unknown, unknown>);
  }

  get(name: string): AgentTool | undefined {
    return this.tools.get(name);
  }

  list(filter?: (t: AgentTool) => boolean): AgentTool[] {
    const all = [...this.tools.values()];
    return filter ? all.filter(filter) : all;
  }

  /**
   * Run a tool by name. Every dispatch writes exactly one audit row. The
   * registry never swallows an error — callers are expected to catch and
   * emit a `tool_result` event with `ok: false` for user-visible failures.
   */
  async dispatch(name: string, ctx: AgentToolCtx, args: unknown): Promise<unknown> {
    const tool = this.get(name);
    if (!tool) throw new Error(`Unknown tool: ${name}`);

    const parsedArgs = tool.argsSchema.parse(args);

    const started =
      typeof performance !== 'undefined' && typeof performance.now === 'function'
        ? performance.now()
        : Date.now();

    let ok = false;
    let result: unknown;
    let errorMsg: string | null = null;

    try {
      result = await tool.invoke(ctx, parsedArgs);
      tool.resultSchema.parse(result);
      ok = true;
      return result;
    } catch (err) {
      errorMsg = err instanceof Error ? err.message : String(err);
      throw err;
    } finally {
      const durationMs = Math.max(
        0,
        Math.round(
          (typeof performance !== 'undefined' && typeof performance.now === 'function'
            ? performance.now()
            : Date.now()) - started,
        ),
      );
      void agentToolInvocationsService.log({
        orgId: ctx.orgId,
        threadId: ctx.threadId,
        toolName: name,
        argsJson: safeStringify(parsedArgs),
        ok,
        resultSummary: ok ? summarize(result) : null,
        error: errorMsg,
        durationMs,
        // Phase 8 · C-48 — audit columns from migration 112. `capability`
        // is denormalised so scans over the audit table don't have to
        // re-look-up the tool definition; plan_id/step_index link a row
        // to its parent plan and approval_mode records whether the user
        // approved manually or the runner executed autonomously.
        capability: tool.capability ?? 'read',
        planId: ctx.planId ?? null,
        stepIndex: ctx.stepIndex ?? null,
        approvalMode: ctx.approvalMode ?? null,
      });
    }
  }
}

/** JSON.stringify that never throws on cycles — audit should not kill dispatch. */
function safeStringify(value: unknown): string {
  try {
    return JSON.stringify(value);
  } catch {
    return '"[unserialisable]"';
  }
}

export const toolRegistry = new ToolRegistry();
